import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const PURPLE = "#7C3AED";

export default function PitchPackageDialog({ open, onClose, context = {}, onCreated }) {
  const [name, setName] = useState("");
  const [summary, setSummary] = useState("");

  useEffect(() => {
    if (!open) return;

    const companyName = String(context.companyName || "Company").trim();
    const jobTitle = String(context.jobTitle || "").trim();
    setName(`${companyName}${jobTitle ? ` — ${jobTitle}` : ""} Pitch Package`);
    setSummary(
      [
        `Recruiting pitch package for ${companyName}.`,
        context.industry ? `Industry: ${context.industry}` : "",
        context.location ? `Location: ${context.location}` : "",
        jobTitle ? `Hiring focus: ${jobTitle}` : "",
      ]
        .filter(Boolean)
        .join("\n")
    );
  }, [context, open]);

  const handleCreate = () => {
    onCreated?.({
      name: name.trim(),
      summary: summary.trim(),
      context,
    });
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "2px solid #A855F7",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle data-popup-header sx={{ px: 3, py: 2.25 }}>
        <Stack data-popup-header direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                borderRadius: 2,
                bgcolor: "rgba(124,58,237,0.1)",
                color: PURPLE,
              }}
            >
              <DescriptionOutlinedIcon />
            </Box>
            <Box>
              <Typography data-popup-title sx={{ fontSize: 18, fontWeight: 700 }}>Create Pitch Package</Typography>
              <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.2 }}>
                Build a pitch package for this lead.
              </Typography>
            </Box>
          </Stack>
          <IconButton data-popup-close aria-label="Close pitch package dialog" onClick={onClose} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: "#F9FAFB", border: "1px solid #EAECF0", mb: 2.25 }}>
          <Typography sx={{ fontSize: 12, color: "#667085", mb: 0.35 }}>Package context</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#344054" }}>
            {[context.companyName, context.jobTitle, context.location].filter(Boolean).join(" · ") || "Current lead"}
          </Typography>
        </Box>
        <Stack spacing={2}>
          <TextField
            label="Package name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Package summary"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            fullWidth
            multiline
            minRows={6}
          />
        </Stack>
      </DialogContent>
      <DialogActions data-popup-footer sx={{ px: 3, py: 2.25, borderTop: "1px solid #EAECF0" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ color: "#475467", textTransform: "none", borderRadius: "999px", px: 2.25 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!name.trim()}
          sx={{ bgcolor: PURPLE, textTransform: "none", borderRadius: "999px", px: 2.5, "&:hover": { bgcolor: "#6D28D9" } }}
        >
          Create Pitch Package
        </Button>
      </DialogActions>
    </Dialog>
  );
}
